import * as React from 'react'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { OFFERING_LIST_ITEMS_MAX, OFFERING_LIST_ITEM_MAX } from '@lib/constants/vendor-profile'

interface Props {
  id: string
  values: readonly string[]
  onChange: (values: string[]) => void
  placeholder: string
}

/**
 * A short list typed one entry at a time: courses, deliverables, equipment.
 * A comma or Enter ends an entry, so a list can be typed or pasted the way the
 * placeholder shows it ("Appetizers, entrée, dessert"); each entry has its own
 * remove.
 *
 * What is still in the box counts as well: while it is there, it is the last of
 * `values`. So leaving the field changes nothing on screen, and saving keeps it.
 * Making it an entry on leaving used to put a new row of entries above the next
 * field just as it was clicked, and the click landed on whatever moved there.
 */
export function ChipListInput({ id, values, onChange, placeholder }: Props) {
  const [draft, setDraft] = React.useState('')
  // Whether the last of `values` is the text in the box, not an entry made.
  const [counted, setCounted] = React.useState(false)
  const made = counted ? values.slice(0, -1) : [...values]
  const full = made.length >= OFFERING_LIST_ITEMS_MAX

  /**
   * Take in the box's new text: what comes before its last comma (or line break,
   * from a paste) becomes entries, and the rest is counted as it is typed. Each
   * entry as long as one may be, none twice, no more than the list holds.
   */
  function type(text: string): void {
    const parts = text.split(/[,\n]/)
    const rest = parts.pop() ?? ''
    const next = [...made]
    const take = (raw: string): boolean => {
      const entry = raw.trim().slice(0, OFFERING_LIST_ITEM_MAX)
      if (entry === '' || next.length >= OFFERING_LIST_ITEMS_MAX || next.includes(entry)) return false
      next.push(entry)
      return true
    }
    for (const part of parts) take(part)
    const restCounts = take(rest)
    onChange(next)
    setCounted(restCounts)
    // No leading space: the one typed after a comma belongs to neither entry.
    setDraft(rest.trimStart())
  }

  /** Enter: the text in the box is an entry already, so the box clears for the next. */
  function finish(): void {
    if (draft.trim() === '') return
    setDraft('')
    setCounted(false)
  }

  /** A pasted list arrives whole, past the one entry's length limit, to be split like a typed one. */
  function paste(event: React.ClipboardEvent<HTMLInputElement>): void {
    const pasted = event.clipboardData.getData('text')
    if (!/[,\n]/.test(pasted)) return
    event.preventDefault()
    const input = event.currentTarget
    type(draft.slice(0, input.selectionStart ?? draft.length) + pasted + draft.slice(input.selectionEnd ?? draft.length))
  }

  return (
    <div className="space-y-2">
      {made.length > 0 ? (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {made.map((value) => (
            <li key={value} className="inline-flex items-center gap-1 rounded-sm bg-surface-sunken py-0.5 pl-2 pr-1 text-xs text-ink">
              {value}
              <button
                type="button"
                aria-label={`Remove ${value}`}
                onClick={() => onChange(values.filter((v) => v !== value))}
                className="grid h-5 w-5 place-items-center rounded text-ink-muted hover:bg-surface-hover hover:text-ink"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <Input
        id={id}
        size="sm"
        value={draft}
        maxLength={OFFERING_LIST_ITEM_MAX}
        disabled={full}
        placeholder={full ? `That's ${String(OFFERING_LIST_ITEMS_MAX)}` : placeholder}
        onChange={(e) => type(e.target.value)}
        onPaste={paste}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
          e.preventDefault()
          finish()
        }}
      />
    </div>
  )
}
